/* Three-act editorial. The page scroll stays native; no wheel/touch interception. */
(() => {
  if (customElements.get('afterdark-editorial')) return;
  customElements.define('afterdark-editorial', class extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.stage = this.querySelector('[data-ae-stage]');
      this.panels = [...this.querySelectorAll('[data-ae-panel]')];
      this.controls = this.querySelector('[data-ae-controls]');
      this.buttons = [...this.querySelectorAll('[data-ae-go]')];
      this.status = this.querySelector('[data-ae-status]');
      if (!this.stage || this.panels.length < 2) return;
      this.active = 0;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.desktop = matchMedia('(min-width: 900px)');
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.controls.hidden = false;
      this.buttons.forEach(button => button.addEventListener('click', () => this.go(Number(button.dataset.aeGo)), options));
      this.onLayout = () => this.layout();
      this.motion.addEventListener('change', this.onLayout, options);
      this.desktop.addEventListener('change', this.onLayout, options);
      window.addEventListener('resize', this.onLayout, options);
      this.schedule = () => {
        if (this.raf || !this.inView) return;
        this.raf = requestAnimationFrame(() => { this.raf = null; this.update(); });
      };
      window.addEventListener('scroll', this.schedule, { ...options, passive: true });
      this.stage.addEventListener('scroll', this.schedule, { ...options, passive: true });
      this.observer = new IntersectionObserver(entries => {
        this.inView = entries[0].isIntersecting;
        if (this.inView) this.schedule();
      }, { rootMargin: '15% 0px' });
      this.observer.observe(this);
      this.layout();
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      if (this.raf) cancelAnimationFrame(this.raf);
      this._ready = false;
    }
    layout() {
      this.pinned = this.desktop.matches && !this.motion.matches;
      this.dataset.enhanced = String(this.pinned);
      this.dataset.mobile = String(!this.desktop.matches);
      if (!this.desktop.matches) this.querySelector('.ae-runway').append(this.controls);
      else this.stage.append(this.controls);
      this.controls.hidden = this.motion.matches;
      this.panels.forEach((panel, i) => {
        panel.inert = this.pinned && i !== this.active;
        if (this.pinned && i !== this.active) panel.setAttribute('aria-hidden', 'true'); else panel.removeAttribute('aria-hidden');
      });
      this.inView = true;
      this.schedule();
    }
    setActive(index, announce = false) {
      index = Math.max(0, Math.min(this.panels.length - 1, index));
      const changed = index !== this.active;
      this.active = index;
      this.panels.forEach((panel, i) => {
        panel.classList.toggle('is-current', i === index);
        panel.inert = this.pinned && i !== index;
        if (this.pinned && i !== index) panel.setAttribute('aria-hidden', 'true'); else panel.removeAttribute('aria-hidden');
      });
      this.buttons.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
      if (announce && changed && this.status) this.status.textContent = this.panels[index].getAttribute('aria-label');
    }
    update() {
      if (this.pinned) {
        const runway = this.querySelector('.ae-runway');
        const rect = runway.getBoundingClientRect();
        const header = parseFloat(getComputedStyle(this.stage).top) || 0;
        const distance = Math.max(1, runway.offsetHeight - this.stage.offsetHeight);
        const progress = Math.max(0, Math.min(1, (header - rect.top) / distance));
        const index = Math.min(this.panels.length - 1, Math.floor(progress * this.panels.length));
        this.setActive(index);
        this.style.setProperty('--ae-scene', progress.toFixed(4));
        const phase = progress * this.panels.length - index;
        const panel = this.panels[index];
        panel.style.setProperty('--ae-shift', (phase * 2 - 1).toFixed(3));
        panel.style.setProperty('--ae-ghost-offset', `${((phase - .5) * -80).toFixed(2)}px`);
      } else if (!this.desktop.matches) {
        const width = this.stage.clientWidth;
        const middle = this.stage.scrollLeft + width / 2;
        const stageLeft = this.stage.getBoundingClientRect().left;
        let nearest = 0, distance = Infinity;
        this.panels.forEach((panel, i) => {
          const r = panel.getBoundingClientRect();
          const center = r.left - stageLeft + this.stage.scrollLeft + r.width / 2;
          const d = Math.abs(middle - center);
          if (d < distance) { distance = d; nearest = i; }
        });
        this.setActive(nearest);
      }
    }
    go(index) {
      if (this.pinned) {
        const runway = this.querySelector('.ae-runway');
        const header = parseFloat(getComputedStyle(this.stage).top) || 0;
        const distance = Math.max(1, runway.offsetHeight - this.stage.offsetHeight);
        const fraction = index === 0 ? .04 : (index + .35) / this.panels.length;
        const top = scrollY + runway.getBoundingClientRect().top - header + distance * fraction;
        this.setActive(index, true);
        window.scrollTo({ top, behavior: 'smooth' });
      } else {
        const panel = this.panels[index];
        const stageRect = this.stage.getBoundingClientRect();
        const rect = panel.getBoundingClientRect();
        const left = this.stage.scrollLeft + rect.left - stageRect.left - (this.stage.clientWidth - rect.width) / 2;
        this.stage.scrollTo({ left, behavior: this.motion.matches ? 'instant' : 'smooth' });
        this.setActive(index, true);
      }
    }
  });
})();
